import React, { useState, useMemo, useEffect } from 'react';
import { 
  Card, Row, Col, Typography, Button, Space, 
  DatePicker, Tooltip, Drawer,
  Table, Tag, Modal, Badge, Image, message,
  Form, Select, Radio, Input, InputNumber, Steps, Upload, Switch, Alert
} from 'antd';
import { 
  InfoCircleOutlined, 
  HistoryOutlined,
  PlusOutlined,
  EyeOutlined,
  AlipayCircleOutlined,
  SearchOutlined,
  ReloadOutlined,
  CopyOutlined,
  CloudUploadOutlined,
  QuestionCircleOutlined,
  AreaChartOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import TransactionDetails from './TransactionDetails';
const { Title, Text, Paragraph } = Typography;

const { RangePicker } = DatePicker;

const ConsumptionAccount = () => {
  const [searchParams] = useSearchParams();
  const [transactionVisible, setTransactionVisible] = useState(false);
  const [txnInitialType, setTxnInitialType] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [initiateVisible, setInitiateVisible] = useState(false);
  const [isOnlinePaid, setIsOnlinePaid] = useState(false);
  const [rechargeMethod, setRechargeMethod] = useState('online');
  const [rechargeSearchForm] = Form.useForm();
  const [rechargeForm] = Form.useForm();

  // 统计图相关的状态
  const [timeUnit, setTimeUnit] = useState('month'); // 'month' | 'week'
  const [chartDateRange, setChartDateRange] = useState([dayjs().subtract(6, 'month'), dayjs()]);

  // 余额监控报警相关的状态
  const [alertEnabled, setAlertEnabled] = useState(false);
  const [alertModalVisible, setAlertModalVisible] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ threshold: 100, phone: '13800138000' });
  const [alertForm] = Form.useForm();

  // 从私有化部署账单「查看明细」跳转进入时：预选「支出-私有云费用」并打开收支明细抽屉
  useEffect(() => {
    const txnType = searchParams.get('txnType');
    if (!txnType) return;
    const [cat, sub] = txnType.split(',');
    if (cat === 'expense' && sub === 'private_cloud') {
      setTxnInitialType([
        ['expense', 'private_cloud', 'cloud_host'],
        ['expense', 'private_cloud', 'cdn'],
        ['expense', 'private_cloud', 'oss'],
        ['expense', 'private_cloud', 'bandwidth'],
      ]);
      setTransactionVisible(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAlertSwitch = (checked) => {
    if (checked) {
      alertForm.setFieldsValue(alertConfig);
      setAlertModalVisible(true);
    } else {
      setAlertEnabled(false);
      message.info('余额监控报警已关闭');
    }
  };

  const handleAlertSave = async () => {
    try {
      const values = await alertForm.validateFields();
      setAlertConfig(values);
      setAlertEnabled(true);
      setAlertModalVisible(false);
      message.success('预警配置已保存');
    } catch (error) {
      console.log('Validate Failed:', error);
    }
  };

  const handleAlertCancel = () => {
    setAlertModalVisible(false);
    setAlertEnabled(false);
  };

  // 模拟充值记录数据
  const rechargeRecords = [
    { id: 'REC20260317001', amount: 1000.00, time: '2026-03-17 10:00:00', method: 'online', platform: '支付宝', callback: '支付成功', status: 'success', auditTime: '2026-03-17 10:05:00' },
    { id: 'REC20260316002', amount: 5000.00, time: '2026-03-16 14:30:00', method: 'offline', voucher: 'https://via.placeholder.com/400x600?text=Voucher+Image', status: 'pending', auditTime: '-' },
    { id: 'REC20260315003', amount: 200.50, time: '2026-03-15 09:12:45', method: 'offline', voucher: 'https://via.placeholder.com/400x600?text=Reject+Voucher', status: 'rejected', reason: '凭证模糊，请重新上传清晰的汇款单据。', auditTime: '2026-03-15 11:00:00' },
    { id: 'REC20260314004', amount: 50.00, time: '2026-03-14 16:20:00', method: 'online', platform: '支付宝', callback: '支付失败', status: 'rejected', reason: '在线支付未成功，系统自动驳回。', auditTime: '2026-03-14 16:25:00' },
    { id: 'REC20260313005', amount: 88.00, time: '2026-03-13 11:45:00', method: 'online', platform: '支付宝', callback: '支付成功', status: 'pending', auditTime: '-' },
  ];

  // 模拟走势图数据
  const trendData = useMemo(() => {
    if (timeUnit === 'month') {
      return [
        { label: '2025-10', recharge: 2000, consume: 1500 },
        { label: '2025-11', recharge: 5000, consume: 4200 },
        { label: '2025-12', recharge: 3000, consume: 4800 },
        { label: '2026-01', recharge: 8000, consume: 6500 },
        { label: '2026-02', recharge: 4000, consume: 5200 },
        { label: '2026-03', recharge: 6000, consume: 4100 },
      ];
    } else {
      return [
        { label: '第10周', recharge: 800, consume: 600 },
        { label: '第11周', recharge: 1200, consume: 1100 },
        { label: '第12周', recharge: 500, consume: 900 },
        { label: '第13周', recharge: 2000, consume: 1500 },
        { label: '第14周', recharge: 1500, consume: 1200 },
        { label: '第15周', recharge: 1000, consume: 1300 },
      ];
    }
  }, [timeUnit]);

  const maxVal = 10000;

  const rechargeColumns = [
    { title: '流水号', dataIndex: 'id', key: 'id' },
    { title: '充值金额', dataIndex: 'amount', key: 'amount', render: (val) => <Text strong>¥ {val.toFixed(2)}</Text> },
    { title: '充值时间', dataIndex: 'time', key: 'time' },
    { 
      title: '充值方式', 
      key: 'method',
      render: (_, record) => (
        <div>
          <div>{record.method === 'online' ? '在线充值' : '线下汇款'}</div>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>
            {record.method === 'online' ? (
              <Space size={4}>
                <span>{record.platform} |</span>
                <AlipayCircleOutlined style={{ color: '#1677ff', fontSize: 14 }} />
                <Badge status={record.callback === '支付成功' ? 'success' : 'error'} text={record.callback} />
              </Space>
            ) : (
              <Button type="link" size="small" style={{ padding: 0 }} icon={<EyeOutlined />} onClick={() => Modal.info({
                title: '汇款凭证',
                width: 600,
                content: <div style={{ textAlign: 'center' }}><Image src={record.voucher} width="100%" /></div>,
                okText: '关闭'
              })}>查看凭证</Button>
            )}
          </div>
        </div>
      )
    },
    { 
      title: '审核状态', 
      dataIndex: 'status', 
      key: 'status',
      render: (s, record) => {
        if (s === 'success') return <Tag color="success">已通过</Tag>;
        if (s === 'pending') return <Tag color="processing">审核中</Tag>;
        return (
          <Space>
            <Tag color="error">已驳回</Tag>
            <Tooltip title={record.reason}><InfoCircleOutlined style={{ color: '#ff4d4f', cursor: 'help' }} /></Tooltip>
          </Space>
        );
      }
    },
    { title: '审核时间', dataIndex: 'auditTime', key: 'auditTime' },
  ];

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success('已复制到剪贴板');
  };

  const handleRechargeSubmit = async () => {
    try {
      await rechargeForm.validateFields();
      if (rechargeMethod === 'online') {
        if (!isOnlinePaid) {
          setIsOnlinePaid(true);
        } else {
          message.success('支付信息提交成功，请等待系统确认');
          setInitiateVisible(false);
          setIsOnlinePaid(false);
        }
      } else {
        message.success('汇款凭证提交成功，请等待平台审核');
        setInitiateVisible(false);
      }
    } catch (error) {
      console.error('Validate Failed:', error);
    }
  };

  return (
    <div style={{ padding: '16px' }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4}>账户总览</Title>
      </div>
      <Card 
        // title={<Text strong style={{ fontSize: 15 }}>总览</Text>}
        bordered={false} 
        style={{ borderRadius: 12, marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}
        bodyStyle={{ padding: '20px' }}
      >
        <Row gutter={16}>
          <Col span={14}>
            <div style={{ background: '#fbfbfc', padding: '20px', borderRadius: 8, border: '1px solid #f0f0f0', height: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <Text type="secondary" style={{ fontSize: 13 }}>账户余额</Text>
                <Space size={8}>
                  <Button size="small" type="primary" ghost style={{ fontSize: 12, borderRadius: 4, height: 28, padding: '0 16px' }} onClick={() => setDrawerVisible(true)}>充值汇款</Button>
                  <Button size="small" style={{ fontSize: 12, borderRadius: 4, height: 28, padding: '0 16px' }} onClick={() => { setTxnInitialType([]); setTransactionVisible(true); }}>收支明细</Button>
                </Space>
              </div>
              <div style={{ marginBottom: 20 }}>
                <Space align="baseline" size={24}>
                  <Text style={{ fontSize: 28, fontWeight: 600, color: 'rgba(0,0,0,0.85)' }}>¥ 4798.80</Text>
                  <Space size={4}>
                    <Text type="secondary" style={{ fontSize: 13 }}>信用额度: <Text strong style={{ color: 'rgba(0,0,0,0.85)' }}>¥ 10000.00</Text></Text>
                    <Tooltip title="账户余额最大欠费不可超出信用额度，若欠费超出云平台资源会被关停，为了保证您的业务正常运行，请务必及时充值！">
                      <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)', fontSize: 13 }} />
                    </Tooltip>
                  </Space>
                </Space>
              </div>
              <div>
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  <Space size={8}>
                    <Text style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>余额监控报警</Text>
                    <Tooltip title="可用余额低于预警阈值后，每天短信提醒一次（最多连续提醒三天）">
                      <QuestionCircleOutlined style={{ color: 'rgba(0,0,0,0.45)', cursor: 'help', fontSize: 12 }} />
                    </Tooltip>
                    <Switch size="small" checked={alertEnabled} onChange={handleAlertSwitch} />
                  </Space>
                  {alertEnabled && (
                    <div style={{ background: '#f5f5f5', padding: '4px 12px', borderRadius: 4 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        账户余额低于 <Text strong style={{ color: '#ff4d4f' }}>{alertConfig.threshold}元</Text>，将会对手机号：<Text strong>{alertConfig.phone}</Text>，发送短信提醒通知。
                      </Text>
                    </div>
                  )}
                </Space>
              </div>
            </div>
          </Col>

          <Col span={10}>
            <div style={{ background: '#fbfbfc', padding: '20px', borderRadius: 8, border: '1px solid #f0f0f0', height: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <Text type="secondary" style={{ fontSize: 13 }}>可开票金额</Text>
                <Button size="small" type="primary" ghost style={{ fontSize: 12, borderRadius: 4, height: 28, padding: '0 16px' }}>去开票</Button>
              </div>
              <div style={{ marginBottom: 20 }}>
                <Text style={{ fontSize: 28, fontWeight: 600, color: 'rgba(0,0,0,0.85)' }}>¥ 49513.70</Text>
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    可开票金额 = 充值累积金额 - 已开票金额 - 开票中金额
                  </Text>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* 套餐底价扣除策略信息条 — 只读，企业侧查看 */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16, borderRadius: 8 }}
        message="套餐底价扣除策略"
        description={
          <div style={{ fontSize: 13, lineHeight: '22px' }}>
            <div>您企业的增值服务订单将按平台配置扣除套餐底价成本，国内与海外策略独立生效。</div>
            <div style={{ marginTop: 4 }}>
              <Text strong>企业赠送套餐：</Text>
              <Text>国内 扣除 / 80.00% · 海外 扣除 / 100.00%</Text>
            </div>
            <div style={{ marginTop: 2 }}>
              <Text strong>App企业自收款：</Text>
              <Text>国内 扣除 / 80.00% · 海外 不扣除</Text>
            </div>
            <div style={{ marginTop: 2 }}>
              <Text strong>App平台代收款：</Text>
              <Text>国内 抽佣 12.00% · 海外 抽佣 8.00%（不实时扣底价，抽佣时参与计算）</Text>
            </div>
            <div style={{ marginTop: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>服务类型可按国内 / 海外单独配置，未单独配置的按各场景默认策略生效。</Text>
            </div>
            <Button type="link" size="small" style={{ padding: 0, marginTop: 4 }} onClick={() => message.info('跳转至企业详情-商务配置页（只读模式）')}>
              查看详情 →
            </Button>
          </div>
        }
      />

      {/* 充值消费走势图 */}
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Space size={12}>
              <AreaChartOutlined style={{ color: '#0052d9' }} />
              <Text strong style={{ fontSize: 15 }}>充值消费走势</Text>
            </Space>
            <Space size={16}>
              <Radio.Group value={timeUnit} onChange={e => setTimeUnit(e.target.value)} size="small" buttonStyle="solid">
                <Radio.Button value="month">按月</Radio.Button>
                <Radio.Button value="week">按周</Radio.Button>
              </Radio.Group>
              <RangePicker size="small" picker={timeUnit} value={chartDateRange} onChange={setChartDateRange} style={{ width: 240, borderRadius: 4 }} />
            </Space>
          </div>
        }
        bordered={false}
        style={{ borderRadius: 12, marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}
        bodyStyle={{ padding: '16px 24px' }}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 50, gap: 32 }}>
          <Space>
            <div style={{ width: 10, height: 10, background: '#63b379', borderRadius: '50%' }} />
            <Text style={{ fontSize: 13, color: 'rgba(0,0,0,0.65)' }}>充值额</Text>
          </Space>
          <Space>
            <div style={{ width: 10, height: 10, background: '#0052d9', borderRadius: '50%' }} />
            <Text style={{ fontSize: 13, color: 'rgba(0,0,0,0.65)' }}>消费额</Text>
          </Space>
        </div>

        <div style={{ height: 240, position: 'relative', paddingLeft: 50, paddingBottom: 40 }}>
          {/* Y轴刻度 */}
          {[10000, 7500, 5000, 2500, 0].map((val, i) => (
            <div key={val} style={{ 
              position: 'absolute', 
              left: 0, 
              bottom: `${(val / maxVal) * 100 + 15}%`, 
              width: '100%',
              borderBottom: i === 4 ? '1px solid #d9d9d9' : '1px dashed #f0f0f0',
              display: 'flex',
              alignItems: 'center'
            }}>
              <span style={{ position: 'absolute', left: -45, fontSize: 11, color: 'rgba(0,0,0,0.45)' }}>¥{val.toLocaleString()}</span>
            </div>
          ))}

          {/* 柱状条容器 */}
          <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-end', height: '100%', width: '100%', position: 'relative', zIndex: 1 }}>
            {trendData.map(item => (
              <div key={item.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, position: 'relative' }}>
                <Space align="end" size={2}>
                  <Tooltip title={`充值: ¥${item.recharge}`}>
                    <div style={{ width: 16, height: `${(item.recharge / maxVal) * 100}%`, background: '#63b379', borderRadius: '2px 2px 0 0', transition: 'all 0.3s' }} />
                  </Tooltip>
                  <Tooltip title={`消费: ¥${item.consume}`}>
                    <div style={{ width: 16, height: `${(item.consume / maxVal) * 100}%`, background: '#0052d9', borderRadius: '2px 2px 0 0', transition: 'all 0.3s' }} />
                  </Tooltip>
                </Space>
                <div style={{ position: 'absolute', bottom: -30, fontSize: 11, color: 'rgba(0,0,0,0.45)', whiteSpace: 'nowrap' }}>
                  {item.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      <Drawer
        title={<Space><HistoryOutlined /><span>充值记录</span></Space>}
        width={1000}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        destroyOnClose
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setInitiateVisible(true)}>发起充值</Button>}
      >
        <div style={{ marginBottom: 24, paddingBottom: 24, borderBottom: '1px solid #f0f0f0' }}>
          <Form form={rechargeSearchForm} layout="inline">
            <Form.Item name="timeRange" label="充值时间"><RangePicker showTime placeholder={['开始时间', '结束时间']} style={{ width: 380 }} /></Form.Item>
            <Form.Item name="status" label="审核状态">
              <Select placeholder="选择状态" style={{ width: 150 }} allowClear>
                <Select.Option value="pending">审核中</Select.Option>
                <Select.Option value="success">已通过</Select.Option>
                <Select.Option value="rejected">已驳回</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item><Space><Button type="primary" icon={<SearchOutlined />} onClick={() => message.loading('搜索中...', 0.5)}>查询</Button><Button icon={<ReloadOutlined />} onClick={() => rechargeSearchForm.resetFields()}>重置</Button></Space></Form.Item>
          </Form>
        </div>
        <Table columns={rechargeColumns} dataSource={rechargeRecords} rowKey="id" pagination={{ showTotal: (total) => `共 ${total} 条记录`, showSizeChanger: true, locale: { items_per_page: '条/页' } }} />
      </Drawer>

      <Drawer
        title="收支明细"
        width="70%"
        onClose={() => { setTransactionVisible(false); setTxnInitialType([]); }}
        open={transactionVisible}
        destroyOnClose
      >
        <TransactionDetails initialType={txnInitialType} />
      </Drawer>

      <Modal
        title={<Title level={4} style={{ margin: 0 }}>发起充值</Title>}
        width={700}
        open={initiateVisible}
        onCancel={() => { setInitiateVisible(false); setIsOnlinePaid(false); }}
        footer={[
          <Button key="back" onClick={() => { setInitiateVisible(false); setIsOnlinePaid(false); }}>取消</Button>,
          <Button key="submit" type="primary" size="large" onClick={handleRechargeSubmit}>{rechargeMethod === 'online' ? (isOnlinePaid ? '提交审核' : '立即支付') : '提交审核'}</Button>
        ]}
        destroyOnClose
      >
        <div style={{ padding: '8px 0' }}>
          {/* <Alert message="若存在信用配额，充值会优先自动补足信用配额，再进行账户余额的充值。" type="warning" showIcon style={{ marginBottom: 24, borderRadius: 8 }} /> */}
          <div style={{ background: '#f5f7fa', padding: '16px 24px', borderRadius: 8, marginBottom: 24, display: 'flex', gap: 40 }}>
            <div><Text type="secondary">账户余额</Text><div style={{ fontSize: 20, fontWeight: 'bold', color: '#262626', marginTop: 4 }}>¥ 160.15</div></div>
            <div><Text type="secondary">信用配额</Text><div style={{ fontSize: 20, fontWeight: 'bold', color: '#ff4d4f', marginTop: 4 }}>¥ 0.00</div></div>
          </div>
          <Form form={rechargeForm} layout="vertical" initialValues={{ currency: 'CNY', method: 'online' }}>
            <Row gutter={24}>
              <Col span={12}><Form.Item label="充值币种" name="currency"><Select disabled options={[{ label: '人民币 (CNY)', value: 'CNY' }]} /></Form.Item></Col>
              <Col span={12}><Form.Item label="充值金额" name="amount" rules={[{ required: true, message: '请输入充值金额' }]}><InputNumber style={{ width: '100%' }} min={0.01} precision={2} placeholder="请输入金额" prefix="¥" /></Form.Item></Col>
            </Row>
            <Form.Item label="充值方式" name="method"><Radio.Group value={rechargeMethod} onChange={e => setRechargeMethod(e.target.value)} optionType="button" buttonStyle="solid"><Radio.Button value="online">在线充值</Radio.Button><Radio.Button value="offline">对公转账</Radio.Button></Radio.Group></Form.Item>
            {rechargeMethod === 'online' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Card size="small" style={{ background: '#fafafa', border: '1px solid #f0f0f0' }}><Space align="center"><Text>支付方式：</Text><Tag color="blue" style={{ padding: '4px 12px', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 8 }}><AlipayCircleOutlined style={{ fontSize: 18 }} /><span style={{ fontWeight: 'bold' }}>支付宝支付</span></Tag></Space></Card>
                {isOnlinePaid && (
                  <div style={{ marginTop: 8, padding: '24px', background: '#fff', border: '1px solid #f0f0f0', borderRadius: 12, textAlign: 'center' }}>
                    <Title level={4}>支付宝扫码支付</Title>
                    <div style={{ padding: '16px', background: '#fff', display: 'inline-block', border: '1px solid #f0f0f0', borderRadius: 8, marginTop: 8 }}><Image src="https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=AlipayDemo" width={160} preview={false} /></div>
                    <div style={{ marginTop: 12, marginBottom: 24 }}><Text type="secondary" style={{ fontSize: 12 }}>使用手机支付宝扫码完成付款</Text></div>
                    <div style={{ textAlign: 'left', background: '#fafafa', padding: '16px', borderRadius: 8 }}>
                      <Row style={{ marginBottom: 8 }}><Col span={8}><Text type="secondary" style={{ fontSize: 13 }}>交易金额</Text></Col><Col span={16}><Text strong style={{ fontSize: 16, color: '#f50' }}>¥ {(rechargeForm.getFieldValue('amount') || 0).toFixed(2)}</Text></Col></Row>
                      <Row style={{ marginBottom: 8 }}><Col span={8}><Text type="secondary" style={{ fontSize: 13 }}>支付状态</Text></Col><Col span={16}><Badge status="processing" text="等待支付结果..." /></Col></Row>
                      <Row><Col span={8}><Text type="secondary" style={{ fontSize: 13 }}>交易单号</Text></Col><Col span={16}><Text style={{ fontSize: 12, fontFamily: 'monospace' }}>ALIPAY_TRX_8b7a9dc2ae7d4b859c</Text></Col></Row>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ marginTop: 16 }}>
                <Title level={4}>汇款流程</Title>
                <div style={{ background: '#f8fafc', padding: '24px', borderRadius: 12, border: '1px solid #e2e8f0' }}>
                  <Steps direction="vertical" size="small" current={1} items={[{ title: '线下汇款', description: (<div style={{ background: '#fff', padding: '16px', borderRadius: 8, marginTop: 12, border: '1px solid #f0f0f0' }}><Text type="secondary" style={{ fontSize: 12 }}>请汇款到平台指定账户：</Text><div style={{ marginTop: 12 }}><Paragraph style={{ marginBottom: 8 }}><Text type="secondary">开户名：</Text><Text strong>南京鹤梦信息技术有限公司</Text><Button type="link" size="small" icon={<CopyOutlined />} onClick={() => copyToClipboard('南京鹤梦信息技术有限公司')} /></Paragraph><Paragraph style={{ marginBottom: 8 }}><Text type="secondary">开户行：</Text><Text strong>中国建设银行股份有限公司南京江宁经济开发区支行</Text></Paragraph><Paragraph style={{ marginBottom: 0 }}><Text type="secondary">账　号：</Text><Text strong style={{ fontSize: 16, color: '#1890ff' }}>3205 0159 6056 0000 0471</Text><Button type="link" size="small" icon={<CopyOutlined />} onClick={() => copyToClipboard('32050159605600000471')} /></Paragraph></div></div>) }, { title: '提交汇款凭证', description: (<div style={{ marginTop: 12 }}><Upload.Dragger maxCount={1} beforeUpload={() => false} style={{ background: '#fff' }}><p className="ant-upload-drag-icon"><CloudUploadOutlined /></p><p className="ant-upload-text">点击或拖拽汇款单据图片至此处上传</p><p className="ant-upload-hint">支持 jpg, png 格式，文件小于 5MB</p></Upload.Dragger></div>) }, { title: '平台确认汇款', description: '汇款后请尽快与我们联系，我们及时为您处理。客服：18936003545' }]} />
                </div>
              </div>
            )}
          </Form>
        </div>
      </Modal>

      <Modal
        title="配置余额监控预警"
        open={alertModalVisible}
        onOk={handleAlertSave}
        onCancel={handleAlertCancel}
        okText="保存"
        cancelText="取消"
        destroyOnClose
        width={480}
      >
        <div style={{ padding: '8px 0' }}>
          <div style={{ background: '#fff7e6', padding: '12px 16px', borderRadius: 8, border: '1px solid #ffe7ba', marginBottom: 20 }}>
            <Space align="start">
              <InfoCircleOutlined style={{ color: '#fa8c16', marginTop: 3 }} />
              <Text style={{ fontSize: 13, color: 'rgba(0,0,0,0.65)' }}>余额低于「提醒阈值」时，每天短信提醒一次（最多提醒3天）。</Text>
            </Space>
          </div>
          <Form form={alertForm} layout="vertical">
            <Form.Item name="threshold" label={<Text strong>预警阈值 (元)</Text>} rules={[{ required: true, message: '请输入预警阈值' }]}><InputNumber style={{ width: '100%' }} placeholder="请输入阈值 (正数或负数)" /></Form.Item>
            <Form.Item name="phone" label={<Text strong>通知手机号</Text>} rules={[{ required: true, message: '请输入手机号' }, { pattern: /^\d+$/, message: '请输入有效的手机号' }]}><Input placeholder="请输入接收短信的手机号" /></Form.Item>
          </Form>
        </div>
      </Modal>
    </div>
  );
};

export default ConsumptionAccount;
